import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  Navigate,
  Route,
  Routes,
  BrowserRouter,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { hotjar } from 'react-hotjar';
import Loading from 'components/Loading';
import FloatingButton from 'components/FloatingButton';
import CourseVideo from 'pages/Members/CourseVideo';
import NewHome from 'pages/NewHome';
import { registerWebMcpTools } from 'agent/webmcp';

const Contact = lazy(() => import('pages/Contact'));
const Hobbies = lazy(() => import('pages/Hobbies'));
const Home = lazy(() => import('pages/Home'));
const NotFound = lazy(() => import('pages/NotFound'));
const Post = lazy(() => import('pages/Post'));
const PageBody = lazy(() => import('pages/PageBody'));
const MyStacks = lazy(() => import('pages/MyStacks'));
const MyProjectsPage = lazy(() => import('pages/MyProjectsPage'));
const About = lazy(() => import('pages/About'));
const LifeTimeline = lazy(() => import('pages/LifeTimeline'));
const LearnProgramming = lazy(() => import('pages/LearnProgramming'));
const CapturePage = lazy(() => import('pages/Capture'));
const Posts = lazy(() => import('pages/Painel'));

const PageBodyMembers = lazy(() => import('pages/Members/PageBodyMembers'));

const MembersArea = lazy(() => import('pages/Members'));
const CourseDetails = lazy(() => import('pages/Members/CourseDetails'));

const Authentication = lazy(() => import('pages/Auth'));

/** Redirects legacy hash URLs (/#/contato) to path URLs (/contato). */
function HashRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const { hash } = window.location;
    if (!hash.startsWith('#/')) return;

    const path = hash.slice(1) || '/';
    window.history.replaceState(null, '', path);
    navigate(path, { replace: true });
  }, [navigate]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [pathname]);

  return null;
}

function AppRoutes() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    setIsAuthenticated(!!token);
  }, []);

  useEffect(() => {
    hotjar.initialize(4997618, 6);
  }, []);

  useEffect(() => {
    let cleanup = () => {};
    registerWebMcpTools().then((unregister) => {
      cleanup = unregister || cleanup;
    });
    return () => cleanup();
  }, []);

  return (
    <BrowserRouter>
      <HashRedirect />
      <ScrollToTop />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<PageBody />}>
            <Route index element={<Home />} />
            <Route path="/sobre" element={<About />} />
            <Route path="/sobre/linha-do-tempo" element={<LifeTimeline />} />
            <Route path="/aprenda-programacao" element={<LearnProgramming />} />
            <Route path="/habilidades" element={<MyStacks />} />
            <Route path="/projetos" element={<MyProjectsPage />} />
            <Route path="/projetos/:id" element={<Post />} />
            <Route path="/contato" element={<Contact />} />
            <Route path="/hobbies" element={<Hobbies />} />
            <Route path="/newhome" element={<NewHome />} />

            <Route
              path="/operador"
              element={
                isAuthenticated ? (
                  <Posts />
                ) : (
                  <Navigate to="/login" replace state={{ from: '/operador' }} />
                )
              }
            />
            <Route
              path="/login"
              element={<Authentication setIsAuthenticated={setIsAuthenticated} />}
            />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route element={<PageBodyMembers />}>
            <Route path="/area-de-mebros" element={<MembersArea />} />
            <Route path="/course-details" element={<CourseDetails />} />
            <Route path="/course-video" element={<CourseVideo />} />
          </Route>
          <Route path="/ebookgratis" element={<CapturePage />} />
        </Routes>
      </Suspense>
      <FloatingButton />
    </BrowserRouter>
  );
}

export default AppRoutes;
